import { z } from 'zod';

import type { RootResourceName } from './payload.schema';
import { NextGenBrokerRoles, UserRole } from './user-role';

export const userSchema = z.object({
  email: z.string(),
  id: z.string(),
  name: z.string(),
  role: z.nativeEnum(UserRole),
  external_role: z.enum([...NextGenBrokerRoles, UserRole.TemplateManager]).optional(),
  tenant_id: z.string().uuid(),
  teams: z.string().optional(),
  team_id: z.string().optional(),
});

export const systemUserSchema = z.object({
  id: z.literal('system'),
  name: z.literal('system'),
  email: z.literal('system@system.com'),
  role: z.literal('system'),
  tenant_id: z.string().uuid(),
  teams: z.string().optional(),
  team_id: z.string().optional(),
});

/** Who a stored record attributes a write to: a tenant user (tenant_id optional) or the system user. */
export const recordUserSchema = userSchema.partial({ tenant_id: true }).or(systemUserSchema);

export const baseAuthorizerSchema = z.object({
  sub: z.string(),
  email: z.string().email(),
  given_name: z.string(),
  family_name: z.string(),
  'custom:role': z.nativeEnum(UserRole),
  'custom:tenant_id': z.string().uuid(),
  'custom:teams': z.string().optional(),
});

export const pplAuthorizerSchema = baseAuthorizerSchema
  .extend({
    session_id: z.string(),
    alias_id: z.string(),
    external_role: z.enum([...NextGenBrokerRoles, UserRole.TemplateManager]),
    company_name: z.string(),
    organisation_name: z.string(),
    organisation_id: z.string(),
    team_id: z.string().optional(),
    team_name: z.string().optional(),
    correlation_id: z.string().optional(),
  })
  .omit({ 'custom:teams': true });

export const pplBrokerAuthorizerSchema = pplAuthorizerSchema.pick({
  alias_id: true,
  sub: true,
  email: true,
  'custom:role': true,
  'custom:tenant_id': true,
  team_id: true,
  correlation_id: true,
});

export const baseDatabaseSchema = z.object({
  created_at: z.string(),
  created_by: recordUserSchema,
  deleted_at: z.nullable(z.string()).optional(),
  deleted_by: recordUserSchema.optional(),
  id: z.string(),
  last_updated_by: recordUserSchema.optional(),
  pk: z.string(),
  sk: z.string(),
  updated_at: z.string().optional(),
  update_type: z.string().optional(),
});

type resourceId = string;
export type NonPathParametersPartitionKeys =
  | `submissions#${resourceId}`
  | `templates#${resourceId}`
  | `submissions#${resourceId}#endorsements#${resourceId}`
  | `submissions#${resourceId}#eval_runs#${resourceId}`
  | `blocks#${resourceId}`
  | 'eval_configs'
  | `eval_configs#${resourceId}`
  | 'counter';
export type PartitionKey = RootResourceName | NonPathParametersPartitionKeys;

export type PK = { pk: PartitionKey };
export type SK = { sk: string };
export type User = z.infer<typeof userSchema>;
export type SystemUser = z.infer<typeof systemUserSchema>;
export type RecordUser = z.infer<typeof recordUserSchema>;
export type DbResource = z.infer<typeof baseDatabaseSchema>;
export type BaseAuthorizer = z.infer<typeof baseAuthorizerSchema>;
export type PPLAuthorizer = z.infer<typeof pplAuthorizerSchema>;
export type PPLBrokerAuthorizer = z.infer<typeof pplBrokerAuthorizerSchema>;
